import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import PlayerInput from './components/PlayerInput';
import GroupSlider from './components/GroupSlider';
import GroupDisplay from './components/GroupDisplay';
import { Player } from './types';
import { storage, STORAGE_KEYS } from './utils/storage';

function App() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [sliderValue, setSliderValue] = useState(2);
  const [sliderMode, setSliderMode] = useState<'groups' | 'playersPerGroup'>('playersPerGroup');
  const [showOrder, setShowOrder] = useState(true);
  const [groups, setGroups] = useState<Player[][]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load data from localStorage on component mount
  useEffect(() => {
    if (storage.isAvailable()) {
      const savedPlayers = storage.get<Player[]>(STORAGE_KEYS.PLAYERS, []);
      const savedSliderValue = storage.get<number>(STORAGE_KEYS.SLIDER_VALUE, 2);
      const savedSliderMode = storage.get<'groups' | 'playersPerGroup'>(STORAGE_KEYS.SLIDER_MODE, 'playersPerGroup');
      const savedShowOrder = storage.get<boolean>(STORAGE_KEYS.SHOW_ORDER, true);
      const savedGroups = storage.get<Player[][]>(STORAGE_KEYS.GROUPS, []);

      console.log('🔄 Loading from localStorage:', {
        players: savedPlayers.length,
        sliderValue: savedSliderValue,
        sliderMode: savedSliderMode,
        groups: savedGroups.length
      });

      if (savedPlayers.length > 0) {
        setPlayers(savedPlayers);
      }
      if (savedSliderValue > 0) {
        setSliderValue(savedSliderValue);
      }
      if (savedSliderMode) {
        setSliderMode(savedSliderMode);
      }
      if (savedShowOrder !== undefined) {
        setShowOrder(savedShowOrder);
      }
      if (savedGroups.length > 0) {
        setGroups(savedGroups);
      }
      
      setIsInitialized(true);
    } else {
      setIsInitialized(true);
    }
  }, []);

  // Save players to localStorage whenever they change
  useEffect(() => {
    if (storage.isAvailable() && isInitialized) {
      storage.set(STORAGE_KEYS.PLAYERS, players);
      console.log('Saving players to localStorage:', players.length);
    }
  }, [players, isInitialized]);

  // Clear groups when players change (separate effect to avoid infinite loop)
  useEffect(() => {
    if (isInitialized && players.length === 0) {
      // Clear groups when there are no players
      setGroups([]);
      if (storage.isAvailable()) {
        storage.set(STORAGE_KEYS.GROUPS, []);
      }
      console.log('Clearing groups due to no players');
    }
  }, [isInitialized, players.length]);

  // Save slider value to localStorage whenever it changes
  useEffect(() => {
    if (storage.isAvailable() && isInitialized) {
      storage.set(STORAGE_KEYS.SLIDER_VALUE, sliderValue);
      console.log('Saving slider value to localStorage:', sliderValue);
    }
  }, [sliderValue, isInitialized]);

  // Save slider mode to localStorage whenever it changes
  useEffect(() => {
    if (storage.isAvailable() && isInitialized) {
      storage.set(STORAGE_KEYS.SLIDER_MODE, sliderMode);
      console.log('Saving slider mode to localStorage:', sliderMode);
    }
  }, [sliderMode, isInitialized]);

  // Save showOrder to localStorage whenever it changes
  useEffect(() => {
    if (storage.isAvailable() && isInitialized) {
      storage.set(STORAGE_KEYS.SHOW_ORDER, showOrder);
      console.log('Saving showOrder to localStorage:', showOrder);
    }
  }, [showOrder, isInitialized]);

  // Save groups to localStorage whenever they change
  useEffect(() => {
    if (storage.isAvailable() && isInitialized) {
      storage.set(STORAGE_KEYS.GROUPS, groups);
      console.log('Saving groups to localStorage:', groups.length);
    }
  }, [groups, isInitialized]);

  // Calculate actual number of groups based on slider value and mode
  const getActualNumGroups = useCallback(() => {
    if (players.length === 0) return 0;
    
    if (sliderMode === 'groups') {
      return sliderValue;
    } else {
      // In players per group mode, calculate groups
      return Math.ceil(players.length / sliderValue);
    }
  }, [players.length, sliderValue, sliderMode]);

  const generateGroups = useCallback(() => {
    if (players.length === 0) return;

    const actualNumGroups = getActualNumGroups();
    if (actualNumGroups === 0) return;

    // Shuffle players randomly
    const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);
    
    // Calculate players per group
    const playersPerGroup = Math.ceil(players.length / actualNumGroups);
    
    // Create groups
    const newGroups: Player[][] = [];
    for (let i = 0; i < actualNumGroups; i++) {
      const startIndex = i * playersPerGroup;
      const endIndex = Math.min(startIndex + playersPerGroup, players.length);
      const group = shuffledPlayers.slice(startIndex, endIndex);
      
      // Add order badges per group (1st, 2nd, 3rd in each group)
      if (showOrder) {
        group.forEach((player, index) => {
          player.order = index + 1; // 1, 2, 3, etc. per group
        });
      } else {
        // Clear order if not showing
        group.forEach((player) => {
          player.order = undefined;
        });
      }
      
      newGroups.push(group);
    }
    
    setGroups(newGroups);
  }, [players, getActualNumGroups, showOrder]);

  // Only generate groups when explicitly requested or when settings change
  useEffect(() => {
    if (isInitialized && players.length > 0) {
      // Check if we have saved groups that match our current settings
      const savedGroups = storage.get<Player[][]>(STORAGE_KEYS.GROUPS, []);
      const savedSliderValue = storage.get<number>(STORAGE_KEYS.SLIDER_VALUE, 2);
      const savedSliderMode = storage.get<'groups' | 'playersPerGroup'>(STORAGE_KEYS.SLIDER_MODE, 'playersPerGroup');
      const savedShowOrder = storage.get<boolean>(STORAGE_KEYS.SHOW_ORDER, true);
      
      // Only regenerate if settings have changed or if we have no groups
      if (savedGroups.length === 0 || 
          savedSliderValue !== sliderValue || 
          savedSliderMode !== sliderMode ||
          savedShowOrder !== showOrder) {
        generateGroups();
      }
    }
  }, [isInitialized, players.length, sliderValue, sliderMode, generateGroups, showOrder]);

  // Handle initial load - restore groups if they exist and settings match
  useEffect(() => {
    if (isInitialized && players.length > 0) {
      const savedGroups = storage.get<Player[][]>(STORAGE_KEYS.GROUPS, []);
      const savedSliderValue = storage.get<number>(STORAGE_KEYS.SLIDER_VALUE, 2);
      const savedSliderMode = storage.get<'groups' | 'playersPerGroup'>(STORAGE_KEYS.SLIDER_MODE, 'playersPerGroup');
      const savedShowOrder = storage.get<boolean>(STORAGE_KEYS.SHOW_ORDER, true);
      
      // If we have saved groups and settings match, restore them
      if (savedGroups.length > 0 && 
          savedSliderValue === sliderValue && 
          savedSliderMode === sliderMode &&
          savedShowOrder === showOrder) {
        setGroups(savedGroups);
        console.log('Restoring groups from localStorage:', savedGroups.length);
      } else if (savedGroups.length > 0 && 
                 savedSliderValue === sliderValue && 
                 savedSliderMode === sliderMode) {
        // Settings match but showOrder changed, regenerate with new order preference
        generateGroups();
      }
    }
  }, [isInitialized, players.length, sliderValue, sliderMode, showOrder, generateGroups]);

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1 className="title">Randomizer</h1>
          <p className="subtitle">Split players into random groups</p>
        </header>

        <div className="main-content">
          <PlayerInput 
            players={players} 
            setPlayers={setPlayers} 
          />
          
          {players.length > 0 && (
            <div className="controls-section">
              <GroupSlider 
                sliderValue={sliderValue}
                setSliderValue={setSliderValue}
                sliderMode={sliderMode}
                setSliderMode={setSliderMode}
                maxGroups={Math.max(2, players.length)}
                totalPlayers={players.length}
                onRegenerateGroups={generateGroups}
              />
              
              <div className="checkbox-container">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showOrder}
                    onChange={(e) => setShowOrder(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkmark"></span>
                  Show player order badges
                </label>
              </div>
            </div>
          )}

          {groups.length > 0 && (
            <GroupDisplay 
              groups={groups} 
              showOrder={showOrder}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
